import React from 'react';
import { OpenAiLogo, AnthropicLogo } from './BrandLogos';
import { LlmProvider, ModelType, getProviderModels } from '../hooks/useModelSelector';

// Purely cosmetic per-provider icon + accent color for the model selector.
// Unknown/future provider ids fall back to a neutral generic look — this is
// presentation only, it has no effect on which providers/models are usable.
const PROVIDER_STYLES: Record<string, { icon: string; gradient: string; color: string }> = {
  openai: { icon: 'fas fa-robot', gradient: '#ffffff', color: '#000000' },
  // Plain white badge, same as OpenAI's — the real logo mark is already
  // orange (#D97757), so an orange gradient badge behind it (the old value
  // here) made the mark nearly invisible against its own background.
  anthropic: { icon: 'fas fa-atom', gradient: '#ffffff', color: '#c15f3c' },
};
const DEFAULT_PROVIDER_STYLE = { icon: 'fas fa-microchip', gradient: 'linear-gradient(135deg, #6b7280, #4b5563)', color: '#6b7280' };
const getProviderStyle = (providerId: string) => PROVIDER_STYLES[providerId] ?? DEFAULT_PROVIDER_STYLE;

// Real brand marks for OpenAI and Anthropic (falls back to the generic
// FontAwesome icon for every other provider).
const ProviderIcon: React.FC<{ providerId: string }> = ({ providerId }) => {
  if (providerId === 'openai') return <OpenAiLogo />;
  if (providerId === 'anthropic') return <AnthropicLogo />;
  return <i className={getProviderStyle(providerId).icon} aria-hidden="true" />;
};

// The tag shown on each picker while more than one is on screen.
export const MODEL_TYPE_LABELS: Record<ModelType, string> = {
  chat: 'Chat',
  image: 'Image',
};

interface ModelPickerProps {
  // Small tag in front of the model name, so stacked pickers can be told
  // apart. Omitted for a lone picker, which looks as it always has.
  label?: string;
  providers: LlmProvider[];
  selectedModelId: string | null;
  // Models of every other provider are disabled while set (see
  // useModelSelector's lockProviderForSend).
  lockedProviderId: string | null;
  isOpen: boolean;
  // Both shared by every picker, and attached only while this picker's menu
  // is open: the menu, and the button that toggles it (which counts as inside
  // the menu for closing on an outside click, so clicking it again closes it).
  menuRef: React.RefObject<HTMLDivElement | null>;
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
  onToggle: () => void;
  onSelect: (modelId: string) => void;
}

// One model picker: the button showing the selected model and its menu of
// providers → tiers → models. The input bar renders one per model type in
// play (useModelSelector's getModelPickerTypes) — always through this one
// component, so stacked pickers look and behave the same. The menu is
// positioned against .input-adornments-right, so every picker's menu opens
// in the same place.
const ModelPicker: React.FC<ModelPickerProps> = ({
  label,
  providers,
  selectedModelId,
  lockedProviderId,
  isOpen,
  menuRef,
  triggerRef,
  onToggle,
  onSelect,
}) => {
  const selectedModel = providers.flatMap(getProviderModels).find((m) => m.id === selectedModelId) ?? null;
  if (!selectedModel) return null;

  const selectedProviderId = providers.find((p) => getProviderModels(p).some((m) => m.id === selectedModelId))?.id ?? '';
  const selectedProviderStyle = getProviderStyle(selectedProviderId);
  const ariaLabel = label ? `Select ${label.toLowerCase()} model` : 'Select model';

  return (
    <>
      <button
        ref={isOpen ? triggerRef : undefined}
        type="button"
        className={`model-trigger-button ${isOpen ? 'open' : ''}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        onClick={onToggle}
      >
        {label && <span className="model-trigger-tag">{label}</span>}
        <span className="model-trigger-icon" style={{ background: selectedProviderStyle.gradient }}>
          <ProviderIcon providerId={selectedProviderId} />
        </span>
        <span className="model-trigger-label">{selectedModel.label}</span>
        <i className={`fas fa-chevron-down model-trigger-chevron ${isOpen ? 'open' : ''}`} aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="model-menu" ref={menuRef} role="menu" aria-label={ariaLabel}>
          {providers.map((provider, i) => {
            const providerDisabled = lockedProviderId !== null && provider.id !== lockedProviderId;
            const providerStyle = getProviderStyle(provider.id);
            return (
              <div className="model-menu-group" key={provider.id}>
                {i > 0 && <div className="model-menu-divider" />}
                <div className="model-menu-group-label">
                  <span className="model-menu-group-icon" style={{ background: providerStyle.gradient }}>
                    <ProviderIcon providerId={provider.id} />
                  </span>
                  {provider.label}
                  {providerDisabled && (
                    <i className="fas fa-lock model-menu-group-lock" aria-hidden="true" title="Locked for this chat" />
                  )}
                </div>
                {provider.tiers.map((tier) => (
                  // Tiers from the older flat response format have no
                  // label, so they render as a bare list of models.
                  <div className="model-menu-tier" key={tier.id} role="group" aria-label={tier.label || undefined}>
                    {tier.label && (
                      <div className="model-menu-tier-header">
                        <span className="model-menu-tier-label">{tier.label}</span>
                        {tier.description && <span className="model-menu-tier-description">{tier.description}</span>}
                      </div>
                    )}
                    <div className="model-menu-tier-models">
                      {tier.models.map((model) => {
                        const isSelected = model.id === selectedModelId;
                        return (
                          <button
                            key={model.id}
                            type="button"
                            role="menuitemradio"
                            aria-checked={isSelected}
                            aria-disabled={providerDisabled}
                            disabled={providerDisabled}
                            className={`model-menu-item ${isSelected ? 'active' : ''} ${providerDisabled ? 'disabled' : ''}`}
                            title={providerDisabled ? `Locked to ${providers.find((p) => p.id === lockedProviderId)?.label ?? 'the current provider'} for this chat` : undefined}
                            onClick={() => {
                              if (providerDisabled) return;
                              onSelect(model.id);
                            }}
                          >
                            <span className="model-menu-item-label">{model.label}</span>
                            {isSelected && <i className="fas fa-check model-menu-item-check" aria-hidden="true" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default ModelPicker;
